import { useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { permissionPayloadToKeys } from "@/features/identity-hub/hooks/usePermissionSelection";
import { permissionTupleKey } from "@/features/identity-hub/lib/grantable-role";

/**
 * Catalog keys the current user may put on a role — you cannot grant what you
 * do not hold. Mirrors the API's `assertCallerCanGrantPermissions`, which unions
 * every access group on the token rather than reading the active tenant alone,
 * and lets super-tenant members through untouched.
 *
 * `null` means "no restriction"; a Set restricts the permission editor to its
 * members.
 */
export function useGrantableCatalogKeys(): ReadonlySet<string> | null {
  const tenants = useAuth((s) => s.tenants);
  const isSuperTenant = useIsSuperTenant();

  return useMemo(() => {
    if (isSuperTenant) return null;
    return permissionPayloadToKeys(tenants.flatMap((tenant) => tenant.permissions));
  }, [tenants, isSuperTenant]);
}

/**
 * Permission tuples the current user may grant on a live role or catalog
 * template. Same union and super-tenant bypass as `useGrantableCatalogKeys`,
 * encoded as `type|method|route` to match the API subset check.
 *
 * `null` means unrestricted.
 */
export function useGrantablePermissionTupleKeys(): ReadonlySet<string> | null {
  const tenants = useAuth((s) => s.tenants);
  const isSuperTenant = useIsSuperTenant();

  return useMemo(() => {
    if (isSuperTenant) return null;
    const keys = new Set<string>();
    for (const tenant of tenants) {
      for (const perm of tenant.permissions) {
        keys.add(permissionTupleKey(perm));
      }
    }
    return keys;
  }, [tenants, isSuperTenant]);
}
